import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { createPost, getPost, updatePost, loadPrefixOptions } from '../../services/boardService';
import { colors } from '../../styles/colors';

export default function BoardWritePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const lang = i18n.language === 'ko' ? 'ko' : 'en';

  const editId = searchParams.get('edit');
  const isEdit = !!editId;

  const isKo = i18n.language === 'ko';
  const isAdmin = user?.isAdmin ?? false;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [prefix, setPrefix] = useState('none');
  const [isNotice, setIsNotice] = useState(false);
  const [isReleaseNote, setIsReleaseNote] = useState(false);
  const [isInfoIconLinked, setIsInfoIconLinked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prefixOptions, setPrefixOptions] = useState<{ key: string; label: string }[]>([]);

  // Load prefix options from DB
  useEffect(() => {
    loadPrefixOptions(lang).then(setPrefixOptions);
  }, [lang]);

  // Load post data when editing
  useEffect(() => {
    if (!editId) return;
    getPost(lang, editId).then((post) => {
      if (post) {
        setTitle(post.title);
        setContent(post.content);
        setPrefix(post.prefix || 'none');
        setIsNotice(post.isNotice ?? false);
        setIsReleaseNote(post.isReleaseNote ?? false);
        setIsInfoIconLinked((post as any).isInfoIconLinked ?? false);
      }
    });
  }, [editId, lang]);

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim() || !user) return;
    // Content filter check
    const { isContentClean, getFilterMessage } = await import('../../services/contentFilter');
    if (!isContentClean(title.trim()) || !isContentClean(content.trim())) {
      alert(getFilterMessage(i18n.language));
      return;
    }
    setSaving(true);
    try {
      if (isEdit && editId) {
        await updatePost(lang, editId, {
          title: title.trim(),
          content: content.trim(),
          prefix,
        });
        navigate(`/board/${editId}`);
      } else {
        await createPost(lang, {
          title: title.trim(),
          content: content.trim(),
          prefix,
          authorId: user.uid,
          authorNickname: user.nickname,
          authorProfileImageUrl: user.profileImageUrl,
          authorGender: user.gender,
          ...(isAdmin ? { isNotice, isReleaseNote, isInfoIconLinked } : {}),
        });
        navigate('/board');
      }
    } catch (err) {
      console.error('[Board] Write failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: `1px solid ${colors.border}` }}>
        <button onClick={() => navigate(-1)} className="text-sm"
          style={{ color: colors.textSecondary }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={saving}
          className="rounded-lg px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: colors.secondary }}>
          {saving ? t('common.loading') : isEdit ? t('common.save') : t('board.write')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Admin options */}
        {isAdmin && (
          <div className="mb-3 flex flex-wrap gap-3">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: colors.error }}>
              <input type="checkbox" checked={isNotice} onChange={(e) => setIsNotice(e.target.checked)} className="accent-red-500" />
              {isKo ? '공지로 등록' : 'Notice'}
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: colors.info }}>
              <input type="checkbox" checked={isInfoIconLinked} onChange={(e) => setIsInfoIconLinked(e.target.checked)} className="accent-blue-500" />
              {isKo ? '정보 아이콘 연결' : 'Info Icon Link'}
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: colors.warning }}>
              <input type="checkbox" checked={isReleaseNote} onChange={(e) => setIsReleaseNote(e.target.checked)} className="accent-amber-500" />
              {isKo ? '릴리즈 노트' : 'Release Note'}
            </label>
          </div>
        )}

        {/* Prefix */}
        <div className="mb-3 flex flex-wrap gap-2">
          {prefixOptions.map((p) => (
            <button key={p.key} onClick={() => setPrefix(p.key)}
              className="rounded px-3 py-1.5 text-xs font-semibold"
              style={{
                backgroundColor: prefix === p.key ? colors.primary : colors.card,
                color: prefix === p.key ? '#fff' : colors.textSecondary,
              }}>
              {p.label}
            </button>
          ))}
        </div>
        <input type="text" placeholder={t('board.titleField')} value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mb-3 w-full rounded-lg px-4 py-3 text-base outline-none"
          style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }} />
        <textarea placeholder={t('board.contentField')} value={content}
          onChange={(e) => setContent(e.target.value)} rows={12}
          className="w-full rounded-lg px-4 py-3 text-sm outline-none"
          style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}`, resize: 'vertical' }} />
      </div>
    </div>
  );
}
